import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/api';

export default function BookingListPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    clientId: '',
    mediaType: '',
    mediaResourceId: '',
    statusId: '',
    ownerId: '',
    bookingSource: 'AGENCY',
    page: 1,
    limit: 50,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      padding: '0.1rem',
      minWidth: '180px',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    })
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const formatReleaseDates = (releaseDates) => {
    if (!releaseDates || !releaseDates.length) return '-';
    return releaseDates.map((r) => formatDate(r.release_date)).join(', ');
  };

  const getMediaTypeColor = (mediaType) => {
    const colors = {
      PRINT: 'print',
      RADIO: 'radio',
      TV: 'tv',
      DIGITAL: 'digital',
    };
    return colors[mediaType?.toUpperCase()] || 'default';
  };

  const getStatusColor = (statusName, notes) => {
    // Check if it's a queued booking first
    if (notes && notes.includes('[Queue Position: Booking')) {
      return 'orange'; // Special color for queued bookings
    }

    const status = statusName?.toUpperCase();
    if (status === 'PENDING') return 'warning';
    if (status === 'CANCELLED') return 'red';
    if (status === 'COMPLETED') return 'green';
    return 'blue';
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/bookings', {
          params: {
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
            clientId: filters.clientId || undefined,
            mediaType: filters.mediaType || undefined,
            mediaResourceId: filters.mediaResourceId || undefined,
            statusId: filters.statusId || undefined,
            ownerId: filters.ownerId || undefined,
            bookingSource: (filters.bookingSource === 'All Sources' || !filters.bookingSource) ? undefined : filters.bookingSource,
            page: filters.page,
            limit: filters.limit,
            includeArchived: false, // Always exclude archived from this list
          },
        });
        setBookings(data.bookings || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [filters]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [statusRes, resourceRes, clientRes, userRes] = await Promise.all([
          api.get('/statuses'),
          api.get('/master/media-resources'),
          api.get('/master/clients'),
          api.get('/users'),
        ]);
        setStatuses(statusRes.data.statuses);
        setResources(resourceRes.data.resources);
        setClients(clientRes.data.clients || []);
        setUsers(userRes.data.users || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchMeta();
  }, []);

  const handleFilterChange = (field) => (event) => {
    const value = event?.target ? event.target.value : event?.value || '';
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: ['dateFrom', 'dateTo', 'clientId', 'mediaType', 'mediaResourceId', 'statusId', 'ownerId', 'bookingSource'].includes(field) ? 1 : prev.page,
    }));
    setSelectedBookings([]);
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    setSelectedBookings([]);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedBookings = [...bookings].sort((a, b) => {
    let aValue, bValue;

    switch (sortConfig.key) {
      case 'client':
        aValue = (a.clientName || a.new_client_name || '').toLowerCase();
        bValue = (b.clientName || b.new_client_name || '').toLowerCase();
        break;
      case 'publication':
        aValue = (a.mediaName || a.platform_details || '').toLowerCase();
        bValue = (b.mediaName || b.platform_details || '').toLowerCase();
        break;
      case 'edition':
        aValue = (a.editionName || '').toLowerCase();
        bValue = (b.editionName || '').toLowerCase();
        break;
      case 'position':
        aValue = (a.positionName || '').toLowerCase();
        bValue = (b.positionName || '').toLowerCase();
        break;
      case 'media_type':
        aValue = (a.media_type || '').toLowerCase();
        bValue = (b.media_type || '').toLowerCase();
        break;
      case 'status':
        aValue = (a.status_name || '').toLowerCase();
        bValue = (b.status_name || '').toLowerCase();
        break;
      case 'owner':
        aValue = (a.ownerName || '').toLowerCase();
        bValue = (b.ownerName || '').toLowerCase();
        break;
      case 'release_dates':
        aValue = a.releaseDates?.[0]?.release_date || '';
        bValue = b.releaseDates?.[0]?.release_date || '';
        break;
      default:
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSelect = (bookingId) => {
    setSelectedBookings((prev) =>
      prev.includes(bookingId) ? prev.filter((id) => id !== bookingId) : [...prev, bookingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBookings.length === bookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(bookings.map((b) => b.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedBookings.length) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedBookings.length} booking(s)?`)) return;

    try {
      await api.delete('/bookings/bulk-delete', { data: { bookingIds: selectedBookings } });
      setSelectedBookings([]);
      // Trigger a refresh of the bookings list
      setFilters((prev) => ({ ...prev, page: 1 }));
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete bookings');
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedBookings.length) return;
    if (!window.confirm(`Are you sure you want to archive ${selectedBookings.length} booking(s)?`)) return;

    try {
      await api.post('/bookings/bulk-archive', { bookingIds: selectedBookings });
      setSelectedBookings([]);
      // Trigger a refresh of the bookings list
      setFilters((prev) => ({ ...prev, page: 1 }));
    } catch (error) {
      console.error('Bulk archive failed:', error);
      alert('Bulk archive failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await api.delete(`/bookings/${bookingId}`);
      // Trigger a refresh of the bookings list
      setFilters((prev) => ({ ...prev, page: 1 }));
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete booking');
    }
  };

  const handleView = (booking) => {
    navigate(`/bookings/view/${booking.id}`);
  };

  const handleEdit = (booking) => {
    navigate(`/bookings/edit/${booking.id}`);
  };

  const handleStatusChange = async (bookingId, newStatusId, releaseId, releaseDate) => {
    const newStatus = statuses.find(s => s.id === parseInt(newStatusId));
    
    if (newStatus?.name === 'Released' && releaseDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const rDate = new Date(releaseDate);
      rDate.setHours(0, 0, 0, 0);
      
      if (today < rDate) {
        alert('You cannot change status to Released before the release date');
        return;
      }
    }

    try {
      if (releaseId) {
        await api.patch(`/bookings/release/${releaseId}/status`, { statusId: newStatusId });
      } else {
        await api.patch(`/bookings/${bookingId}/status`, { statusId: newStatusId });
      }
      
      // Update local state to reflect the change immediately
      setBookings(prev => prev.map(b => {
        if (b.id === bookingId) {
          const newStatus = statuses.find(s => s.id === parseInt(newStatusId));
          
          // If we updated a specific release, update it in the releaseDates array
          let newReleaseDates = b.releaseDates;
          if (releaseId && b.releaseDates) {
            newReleaseDates = b.releaseDates.map(r => 
              r.id === releaseId ? { ...r, status_id: newStatusId, status_name: newStatus?.name } : r
            );
          }
          
          return { 
            ...b, 
            status_id: releaseId ? b.status_id : newStatusId, 
            status_name: releaseId ? b.status_name : newStatus?.name,
            releaseDates: newReleaseDates
          };
        }
        return b;
      }));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleExportAll = () => {
    if (!bookings.length) return alert('No data to export');

    const dataToExport = bookings.map((booking) => ({
      CLIENT: booking.clientName || '-',
      PUBLICATION: booking.mediaName || booking.platform_details || '-',
      EDITION: booking.editionName || '-',
      POSITION: booking.positionName || '-',
      'MEDIA TYPE': booking.media_type || '-',
      STATUS: booking.status_name || '-',
      OWNER: booking.ownerName || '-',
      'Release Date(s)': formatReleaseDates(booking.releaseDates),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');
    XLSX.writeFile(workbook, `All_Bookings_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const totalPages = Math.ceil(totalCount / filters.limit);
  const startItem = totalCount === 0 ? 0 : (filters.page - 1) * filters.limit + 1;
  const endItem = Math.min(filters.page * filters.limit, totalCount);

  const clientOptions = [
    { value: '', label: 'All Clients' },
    ...clients.map(c => ({ value: c.id, label: c.name }))
  ];

  const mediaTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'PRINT', label: 'Print' },
    { value: 'RADIO', label: 'Radio' },
    { value: 'TV', label: 'TV' },
    { value: 'DIGITAL', label: 'Digital' },
  ];

  const publicationOptions = [
    { value: '', label: 'All Publications' },
    ...resources.map(r => ({ value: r.id, label: r.name }))
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...statuses.map(s => ({ value: s.id, label: s.name }))
  ];

  const ownerOptions = [
    { value: '', label: 'All Owners' },
    ...users.map(u => ({ value: u.id, label: u.name || u.email }))
  ];

  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'AGENCY', label: 'Agency' },
    { value: 'PUBLICATION', label: 'Publication' },
  ];

  const getRowStyle = (booking) => {
    const type = booking.media_type?.toUpperCase();
    const mediaColors = {
      PRINT: '#f5f3ff',   // purple-50
      RADIO: '#ecfeff',   // cyan-50
      TV: '#fff7ed',      // orange-50
      DIGITAL: '#ecfdf5', // emerald-50 (distinct from print purple)
    };

    if (booking.notes && booking.notes.includes('[Queue Position: Booking')) {
      return { backgroundColor: '#fffaf0' }; // Light orange for queued
    }
    if (booking.booking_source === 'PUBLICATION') {
      return { backgroundColor: '#f0fdf4' }; // Light green for publication
    }
    
    if (mediaColors[type]) {
      return { backgroundColor: mediaColors[type] };
    }

    return {};
  };

  const flattenedBookings = useMemo(() => {
    const rows = [];
    sortedBookings.forEach(booking => {
      if (booking.releaseDates && booking.releaseDates.length > 0) {
        booking.releaseDates.forEach(release => {
          // Skip archived releases - check for both 1 and true
          if (release.is_archived === 1 || release.is_archived === true || release.is_archived === '1') return;

          // Client-side date filtering to match the search criteria
          // Use local date parts to avoid UTC shift issues
          const d = new Date(release.release_date);
          const releaseDateStr = release.release_date ? 
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : 
            null;
          
          if (filters.dateFrom && releaseDateStr && releaseDateStr < filters.dateFrom) return;
          if (filters.dateTo && releaseDateStr && releaseDateStr > filters.dateTo) return;

          // Also filter by status if status filter is active
          if (filters.statusId && parseInt(release.status_id) !== parseInt(filters.statusId)) return;

          rows.push({
            ...booking,
            displayReleaseDate: release.release_date,
            displayStatusId: release.status_id,
            displayStatusName: release.status_name,
            displayReleaseId: release.id,
            rowKey: `${booking.id}-${release.id}`
          });
        });
      } else {
        // If no release dates, it shouldn't really show up if date filters are active, 
        // but we'll keep the existing logic for safety.
        if (filters.dateFrom || filters.dateTo) return;

        // For the main list, we only show non-archived bookings
        if (booking.is_archived) return;

        rows.push({
          ...booking,
          displayReleaseDate: null,
          displayStatusId: booking.status_id,
          displayStatusName: booking.status_name,
          displayReleaseId: null,
          rowKey: `${booking.id}-no-release`
        });
      }
    });
    return rows;
  }, [sortedBookings, filters.dateFrom, filters.dateTo]);

  return (
    <div className="page-content">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>Booking List</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>Manage and track all media bookings</p>
          </div>
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
            {selectedBookings.length > 0 && (
              <>
                <button className="btn-secondary" onClick={handleBulkArchive} style={{ color: '#6366f1', borderColor: '#c7d2fe' }}>
                  📦 Archive Selected ({selectedBookings.length})
                </button>
                <button className="btn-secondary" onClick={handleBulkDelete} style={{ color: '#ef4444', borderColor: '#fecaca' }}>
                  🗑️ Delete Selected ({selectedBookings.length})
                </button>
              </>
            )}
            <button className="btn-secondary" onClick={handleExportAll}>
              📥 Export All
            </button>
          </div>
        </div>

        <div className="reports-filters-card" style={{ marginBottom: '1.5rem', padding: '1.25rem', background: '#f8fafc', borderRadius: '1rem', border: '1px solid #f1f5f9' }}>
          <div className="filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Date From</label>
              <DatePicker
                selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                onChange={(date) => {
                  const formattedDate = date ? 
                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : 
                    '';
                  handleFilterChange('dateFrom')({ target: { value: formattedDate } });
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="datepicker-input"
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Date To</label>
              <DatePicker
                selected={filters.dateTo ? new Date(filters.dateTo) : null}
                onChange={(date) => {
                  const formattedDate = date ? 
                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : 
                    '';
                  handleFilterChange('dateTo')({ target: { value: formattedDate } });
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="datepicker-input"
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Client</label>
              <Select
                options={clientOptions}
                value={clientOptions.find(opt => opt.value === filters.clientId)}
                onChange={handleFilterChange('clientId')}
                placeholder="Search Client..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Media Type</label>
              <Select
                options={mediaTypeOptions}
                value={mediaTypeOptions.find(opt => opt.value === filters.mediaType)}
                onChange={handleFilterChange('mediaType')}
                placeholder="Search Type..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Status</label>
              <Select
                options={statusOptions}
                value={statusOptions.find(opt => opt.value === filters.statusId)}
                onChange={handleFilterChange('statusId')}
                placeholder="Search Status..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Publication</label>
              <Select
                options={publicationOptions}
                value={publicationOptions.find(opt => opt.value === filters.mediaResourceId)}
                onChange={handleFilterChange('mediaResourceId')}
                placeholder="Search Publication..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Owner</label>
              <Select
                options={ownerOptions}
                value={ownerOptions.find(opt => opt.value === filters.ownerId)}
                onChange={handleFilterChange('ownerId')}
                placeholder="Search Owner..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Source</label>
              <Select
                options={sourceOptions}
                value={sourceOptions.find(opt => opt.value === filters.bookingSource)}
                onChange={handleFilterChange('bookingSource')}
                placeholder="Search Source..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="scroll-container">
          <table className="booking-list-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={bookings.length > 0 && selectedBookings.length === bookings.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('client')} style={{ cursor: 'pointer' }}>
                  CLIENT {sortConfig.key === 'client' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('publication')} style={{ cursor: 'pointer' }}>
                  PUBLICATION {sortConfig.key === 'publication' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('edition')} style={{ cursor: 'pointer' }}>
                  EDITION {sortConfig.key === 'edition' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>
                  POSITION {sortConfig.key === 'position' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('media_type')} style={{ cursor: 'pointer' }}>
                  MEDIA TYPE {sortConfig.key === 'media_type' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  STATUS {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('owner')} style={{ cursor: 'pointer' }}>
                  OWNER {sortConfig.key === 'owner' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('release_dates')} style={{ cursor: 'pointer' }}>
                  Release Date {sortConfig.key === 'release_dates' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10">Loading bookings...</td>
                </tr>
              ) : flattenedBookings.length ? (
                flattenedBookings.map((booking) => (
                  <tr key={booking.rowKey} style={getRowStyle(booking)}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => toggleSelect(booking.id)}
                      />
                    </td>
                    <td>{booking.clientName || booking.new_client_name || '-'}</td>
                    <td>{booking.mediaName || booking.platform_details || '-'}</td>
                    <td>{booking.editionName || '-'}</td>
                    <td>{booking.positionName || '-'}</td>
                    <td>
                      <span className={`media-type-tag media-type-${getMediaTypeColor(booking.media_type)}`}>
                        {booking.media_type || '-'}
                      </span>
                    </td>
                    <td>
                      {booking.booking_source === 'PUBLICATION' ? (
                        '-'
                      ) : (
                        <select
                          value={booking.displayStatusId}
                          onChange={(e) => handleStatusChange(booking.id, e.target.value, booking.displayReleaseId, booking.displayReleaseDate)}
                          className={`pill pill-${getStatusColor(booking.displayStatusName, booking.notes)}`}
                          style={{
                            border: booking.notes && booking.notes.includes('[Queue Position: Booking') ? '2px solid #f97316' : 'none',
                            fontWeight: booking.notes && booking.notes.includes('[Queue Position: Booking') ? 'bold' : 'normal',
                            cursor: 'pointer',
                            appearance: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            width: 'auto',
                            minWidth: '100px'
                          }}
                        >
                          {statuses.map(status => (
                            <option key={status.id} value={status.id}>
                              {booking.notes && booking.notes.includes('[Queue Position: Booking') && status.name === booking.displayStatusName
                                ? `QUEUED: ${booking.notes.match(/Booking \d+/)[0]}`
                                : status.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>{booking.ownerName || '-'}</td>
                    <td>{formatDate(booking.displayReleaseDate)}</td>
                    <td className="actions-cell">
                      <button className="action-btn" onClick={() => handleView(booking)} title="View">
                        👁️
                      </button>
                      <button className="action-btn" onClick={() => handleEdit(booking)} title="Edit">
                        ✏️
                      </button>
                      <button className="action-btn" onClick={() => handleDelete(booking.id)} title="Delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">No bookings found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {startItem} to {endItem} of {totalCount} bookings
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              disabled={filters.page === 1}
              onClick={() => handlePageChange(filters.page - 1)}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (filters.page <= 3) {
                pageNum = i + 1;
              } else if (filters.page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = filters.page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${filters.page === pageNum ? 'active' : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="pagination-btn"
              disabled={filters.page >= totalPages}
              onClick={() => handlePageChange(filters.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
